import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc } from './growth-datetime.util';
import { GrowthOptimizationLiftMeasurement, GrowthOptimizationRun, GrowthOptimizationService, safeJsonObject } from './growth-optimization.service';

@Component({
  standalone: true,
  selector: 'ain-growth-optimizer-run-detail',
  imports: [
    FormsModule,
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    ButtonModule,
    InputTextModule,
    AinDatePipe
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.optimization.runDetail.title')" [description]="t('growth.optimization.runDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.optimization.runDetail.help.title'"
        [descriptionKey]="'growth.optimization.runDetail.help.description'"
        [bulletKeys]="['growth.optimization.runDetail.help.bullets.workflow', 'growth.optimization.runDetail.help.bullets.simulation']" />

      <div class="toolbar">
        <a routerLink="/growth/optimizer/optimization-runs" pButton severity="secondary" [label]="t('catalog.action.backToList')"></a>
        <button pButton type="button" severity="secondary" [label]="t('growth.optimization.actions.requestApproval')" (click)="requestApproval()"></button>
        <button pButton type="button" severity="secondary" [label]="t('growth.optimization.actions.execute')" (click)="execute()"></button>
        <button pButton type="button" severity="danger" [label]="t('growth.optimization.actions.cancel')" (click)="cancel()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!run()) {
        <ain-state type="empty" [title]="t('common.notFound')" />
      } @else {
        <section class="card">
          <div class="grid">
            <div><strong>{{ t('growth.optimization.runDetail.fields.status') }}:</strong> {{ run()!.status }}</div>
            <div><strong>{{ t('growth.optimization.runDetail.fields.workflowProcess') }}:</strong> {{ run()!.workflowProcessInstanceId ?? '-' }}</div>
            <div><strong>{{ t('growth.optimization.runDetail.fields.startedAt') }}:</strong> {{ run()!.startedAtUtc | ainDate:'datetime' }}</div>
            <div><strong>{{ t('growth.optimization.runDetail.fields.completedAt') }}:</strong> {{ run()!.completedAtUtc ? (run()!.completedAtUtc | ainDate:'datetime') : '-' }}</div>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.optimization.runDetail.sections.recommendation') }}</h3>
          @for (entry of objectEntries(run()!.recommendationJson); track entry.key) {
            <p><strong>{{ entry.key }}:</strong> {{ entry.value }}</p>
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.optimization.runDetail.sections.simulation') }}</h3>
          @if (run()!.growthScenarioId) {
            <p>
              <strong>{{ t('growth.optimization.runDetail.fields.scenario') }}:</strong>
              <a [routerLink]="['/growth/twin/scenarios', run()!.growthScenarioId]">{{ run()!.growthScenarioId }}</a>
            </p>
          }
          @for (entry of objectEntries(run()!.simulateResultJson); track entry.key) {
            <p><strong>{{ entry.key }}:</strong> {{ entry.value }}</p>
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.optimization.runDetail.sections.guardrails') }}</h3>
          @for (entry of objectEntries(run()!.guardrailResultJson); track entry.key) {
            <p><strong>{{ entry.key }}:</strong> {{ entry.value }}</p>
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.optimization.runDetail.sections.execution') }}</h3>
          @for (entry of objectEntries(run()!.executionResultJson); track entry.key) {
            <p><strong>{{ entry.key }}:</strong> {{ entry.value }}</p>
          }
        </section>

        <section class="card">
          <h3>{{ t('growth.optimization.runDetail.sections.lift') }}</h3>
          <div class="grid">
            <ain-form-field [label]="t('growth.optimization.runDetail.fields.liftPeriodStart')">
              <ain-date-input [(ngModel)]="liftForm.periodStartUtc" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.runDetail.fields.liftPeriodEnd')">
              <ain-date-input [(ngModel)]="liftForm.periodEndUtc" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.runDetail.fields.baselineValue')">
              <input pInputText [(ngModel)]="liftForm.baselineValue" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.runDetail.fields.resultValue')">
              <input pInputText [(ngModel)]="liftForm.resultValue" />
            </ain-form-field>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('growth.optimization.actions.measureLift')" (click)="measureLift()"></button>
          </div>
          @if (lift()) {
            <p><strong>{{ t('growth.optimization.runDetail.fields.liftPercent') }}:</strong> {{ lift()!.liftPercent ?? '-' }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 8px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .actions { display: flex; gap: 8px; }
  `]
})
export class GrowthOptimizerRunDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(GrowthOptimizationService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly run = signal<GrowthOptimizationRun | null>(null);
  readonly lift = signal<GrowthOptimizationLiftMeasurement | null>(null);

  private runId = '';

  liftForm = {
    periodStartUtc: defaultCalendarFromUtc(),
    periodEndUtc: defaultCalendarToUtc(),
    baselineValue: '100',
    resultValue: '110'
  };

  t(key: string): string { return this.i18n.translate(key); }

  ngOnInit(): void {
    this.runId = this.route.snapshot.paramMap.get('runId') ?? '';
    this.load();
  }

  load(): void {
    if (!this.runId) return;
    this.loading.set(true);
    this.service.getRun(this.runId).subscribe({
      next: row => {
        this.run.set(row);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
    this.service.getLift(this.runId).subscribe({
      next: row => this.lift.set(row),
      error: () => this.lift.set(null)
    });
  }

  objectEntries(json: string): Array<{ key: string; value: string }> {
    const object = safeJsonObject(json);
    return Object.keys(object).map(key => ({ key, value: `${object[key]}` }));
  }

  requestApproval(): void {
    if (!this.runId) return;
    this.service.requestApproval(this.runId).subscribe({ next: row => this.run.set(row) });
  }

  execute(): void {
    if (!this.runId) return;
    this.service.executeRun(this.runId).subscribe({ next: row => this.run.set(row) });
  }

  cancel(): void {
    if (!this.runId) return;
    this.service.cancelRun(this.runId).subscribe({ next: row => this.run.set(row) });
  }

  measureLift(): void {
    if (!this.runId) return;
    this.service.measureLift(this.runId, {
      periodStartUtc: combineUtcDateAndTime(this.liftForm.periodStartUtc, '00:00') ?? new Date().toISOString(),
      periodEndUtc: combineUtcDateAndTime(this.liftForm.periodEndUtc, '23:59') ?? new Date().toISOString(),
      baselineMetricJson: JSON.stringify({ value: Number(this.liftForm.baselineValue || '0') }),
      resultMetricJson: JSON.stringify({ value: Number(this.liftForm.resultValue || '0') })
    }).subscribe({
      next: row => this.lift.set(row)
    });
  }
}
